import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {styled as muiStyled} from '@mui/material/styles';
import {COLORS, FONTS, SIZES, Typography} from '@fupa/fupa-uikit';
import {Check, TouchApp} from '@mui/icons-material';
import {withDropShadow} from 'app/components/tv/customControls/SharedCustomControlsStyles';
import {Button} from '@mui/material';
import {getElementFromLocalStorage, setElementToLocalStorage} from 'app/helpers/webStorageUtility';

const Wrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10000;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const AnimationBackgroundContainer = styled.div`
  position: relative;
  background: no-repeat center/100% url('/onboarding.svg');
  width: 25%;
  aspect-ratio: 16/9;
  @media only screen and (orientation: portrait) {
    aspect-ratio: 9/16;
    background: no-repeat center/contain url('/onboarding-animation-portrait.svg');
  }
`;

const Container = styled.div`
  position: absolute;
  top: 9%;
  bottom: 9%;
  left: 7%;
  right: 7%;
  display: flex;
  @media only screen and (orientation: portrait) {
    top: 7%;
    bottom: 7%;
    left: 10%;
    right: 10%;
  }
`;

const Box = styled.div`
  background-color: transparent;
  opacity: 0.5;
  &:last-child {
    animation: box1 10s infinite;
  }
  &:nth-child(2) {
    animation: box2 10s infinite;
  }
  &:first-child {
    animation: box3 10s infinite;
  }
  @keyframes box1 {
    0% {
      background-color: ${COLORS.white};
    }
    25% {
      background-color: ${COLORS.white};
    }
    30% {
      background-color: transparent;
    }
  }
  @keyframes box2 {
    25% {
      background-color: transparent;
    }
    30% {
      background-color: ${COLORS.white};
    }
    55% {
      background-color: ${COLORS.white};
    }
    60% {
      background-color: transparent;
    }
  }
  @keyframes box3 {
    55% {
      background-color: transparent;
    }
    60% {
      background-color: ${COLORS.white};
    }
    75% {
      background-color: ${COLORS.white};
    }
    80% {
      background-color: transparent;
    }
  }
`;

const SmallBox = styled(Box)`
  flex: 1 20%;
`;

const BigBox = styled(Box)`
  flex: 1 60%;
`;

const AnimationElement = styled.div`
  position: absolute;
  top: 40%;
  @media only screen and (max-width: 600px) {
    --animation-pressed: ${SIZES['16']};
    --animation-default: ${SIZES['24']};
    --position-left: 8%;
    --position-mid: 40%;
    --position-right: 75%;
  }
  animation: animation 10s infinite;

  @keyframes animation {
    0% {
      left: var(--position-right, 80%);
      font-size: var(--animation-default, ${SIZES['32']});
    }
    10% {
      left: var(--position-right, 80%);
      font-size: var(--animation-pressed, ${SIZES['24']});
    }
    20% {
      left: var(--position-right, 80%);
      font-size: var(--animation-default, ${SIZES['32']});
    }
    30% {
      left: var(--position-mid, 45%);
      font-size: var(--animation-default, ${SIZES['32']});
    }
    40% {
      left: var(--position-mid, 45%);
      font-size: var(--animation-pressed, ${SIZES['24']});
    }
    50% {
      left: var(--position-mid, 45%);
      font-size: var(--animation-default, ${SIZES['32']});
    }
    60% {
      left: var(--position-left, 10%);
      font-size: var(--animation-default, ${SIZES['32']});
    }
    70% {
      left: var(--position-left, 10%);
      font-size: var(--animation-pressed, ${SIZES['24']});
    }
    80% {
      left: var(--position-left, 10%);
      font-size: var(--animation-default, ${SIZES['32']});
    }
    100% {
      left: var(--position-right, 80%);
      font-size: var(--animation-default, ${SIZES['32']});
    }
  }
`;

const TextContainer = styled.div`
  position: relative;
  width: 100%;
  height: ${SIZES['32']};
  margin-top: ${SIZES['16']};
  text-align: center;
`;

const StyledTypography = styled(Typography)`
  font-size: ${FONTS['16']};
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  visibility: hidden;
  &:first-child {
    animation: textanim1 10s infinite;
  }
  &:nth-child(2) {
    animation: textanim2 10s infinite;
  }
  &:last-child {
    animation: textanim3 10s infinite;
  }

  @keyframes textanim1 {
    0% {
      visibility: visible;
    }
    25% {
      visibility: hidden;
    }
  }
  @keyframes textanim2 {
    25% {
      visibility: hidden;
    }
    30% {
      visibility: visible;
    }
    55% {
      visibility: hidden;
    }
  }
  @keyframes textanim3 {
    55% {
      visibility: hidden;
    }
    60% {
      visibility: visible;
    }
    75% {
      visibility: hidden;
    }
`;

const ButtonContainer = styled.div`
  width: 100%;
  margin-top: ${SIZES['48']};
  display: flex;
  justify-content: center;
`;

const StyledButton = muiStyled(Button)({
  border: `1px solid ${COLORS.white}!important`,
  color: `${COLORS.white}!important`,
  display: 'inline-flex!important',
  fontSize: `${FONTS['14']}!important`,
  lineHeight: '1.75!important',
  textTransform: 'uppercase!important',
});

const STORAGE_KEY = 'video-onboard';
const StyledTouchApp = withDropShadow(TouchApp);
export const Onboarding = ({isMobile}) => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isMobile && !getElementFromLocalStorage(STORAGE_KEY)) setShow(true);
  }, [isMobile]);

  const handleButtonClick = () => {
    setElementToLocalStorage(STORAGE_KEY, '1');
    setShow(false);
  };

  return show ? (
    <Wrapper>
      <AnimationBackgroundContainer>
        <Container>
          <SmallBox />
          <BigBox />
          <SmallBox />
        </Container>
        <AnimationElement>
          <StyledTouchApp />
        </AnimationElement>
      </AnimationBackgroundContainer>
      <TextContainer>
        <StyledTypography variant='title1'>Für nächstes Video rechts tippen.</StyledTypography>
        <StyledTypography variant='title1'>Zum Pausieren/Fortsetzen mittig tippen.</StyledTypography>
        <StyledTypography variant='title1'>Für vorheriges Video links tippen.</StyledTypography>
      </TextContainer>
      <ButtonContainer>
        <StyledButton variant='outlined' startIcon={<Check />} onClick={handleButtonClick}>
          Verstanden
        </StyledButton>
      </ButtonContainer>
    </Wrapper>
  ) : null;
};
